<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '../../../types/site'

const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

/**
 * Upload goes through the editor adapter provided by the settings panel, so
 * this form has no direct dependency on the app's upload repository.
 */
const { upload } = useSiteEditorAdapters()
const toast = useToast()

const mode = computed<'upload' | 'paste'>(() =>
  props.block.data.html && !props.block.data.url ? 'paste' : 'upload'
)

const setMode = (next: 'upload' | 'paste') => {
  if (next === 'upload') props.block.data.html = ''
  else props.block.data.url = ''
}

const fileInput = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const pickFile = () => fileInput.value?.click()

const onUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (!file) return

  uploading.value = true
  try {
    const result = await upload(file, 'html')
    props.block.data.url = result.url
    props.block.data.html = ''
  } catch (e) {
    // An adapter that already told the user (in its own words and locale)
    // marks the error `handled`, so we don't stack a second toast on top.
    if (!(e as { handled?: boolean })?.handled) {
      const message = (e as { data?: { message?: string } })?.data?.message || (e as Error)?.message
      toast.add({ title: message || t('site.f.custom_html.upload_failed', 'HTML upload failed.'), color: 'error' })
    }
  } finally {
    uploading.value = false
    target.value = ''
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.custom_html.content', 'Content')">
    <SiteEditorOptionToggle
      :model-value="mode"
      :options="[{ value: 'upload', label: t('site.f.custom_html.opt_upload_file', 'Upload file') }, { value: 'paste', label: t('site.f.custom_html.opt_paste_html', 'Paste HTML') }]"
      @update:model-value="setMode($event as 'upload' | 'paste')"
    />
  </UFormField>

  <UFormField
    v-if="mode === 'upload'"
    :label="t('site.f.custom_html.html_file', 'HTML file')"
    :error="!block.data.url ? t('site.f.custom_html.err_source', 'This block needs a file or pasted HTML before the page can be saved.') : undefined"
  >
    <div class="flex items-center gap-2">
      <UButton
        size="sm"
        variant="outline"
        color="neutral"
        icon="i-lucide-upload"
        :loading="uploading"
        @click="pickFile"
      >
        {{ t('site.f.custom_html.upload_button', 'Upload') }}
      </UButton>
      <span
        v-if="block.data.url"
        class="truncate text-xs text-muted"
      >{{ block.data.url }}</span>
    </div>
  </UFormField>

  <UFormField
    v-else
    :label="t('site.f.custom_html.html', 'HTML')"
    :error="!block.data.html ? t('site.f.custom_html.err_source', 'This block needs a file or pasted HTML before the page can be saved.') : undefined"
  >
    <UTextarea
      :model-value="block.data.html ?? ''"
      :rows="8"
      class="w-full font-mono text-xs"
      :placeholder="t('site.f.custom_html.div_your_markup_div', '<div>Your markup…</div>')"
      @update:model-value="block.data.html = String($event)"
    />
  </UFormField>

  <p class="text-xs text-muted">
    {{ t('site.f.custom_html.sandbox_notice', "Runs sandboxed — this content can execute its own scripts but can't read cookies, local storage, or anything else on your site. Only add HTML you trust.") }}
  </p>

  <input
    ref="fileInput"
    type="file"
    accept=".html,.htm,text/html"
    class="hidden"
    @change="onUpload"
  >
</template>
